import React, { useState, useContext } from 'react';
import { FaEdit, FaUsers, FaPlus, FaCopy, FaTrash } from 'react-icons/fa';
import SubsectionPanel from '../../common/SubsectionPanel/SubsectionPanel';
import PersonCard from '../../common/SubsectionPanel/PersonCard';
import PeopleManagement from '../../common/SubsectionPanel/PeopleManagement';
import GenericFlyoutPanel from '../../common/GenericFlyoutPanel/GenericFlyoutPanel';
import PopupDialog from '../../common/PopupDialog/PopupDialog';
import '../../../styles/GenericSiteStyles.scss';
import './ProjectGeneral.scss';
import RoleGuard from '../../common/RoleGuard';
import { UserRoles } from '../../../constants/UserRoles';
import { ProjectScenariosProvider, useProjectScenarios } from '../../../context/ProjectScenariosContext';
import { ProjectScenario, ProjectsScenariosRestDataProvider } from '../../../data/ProjectScenariosDataProvider';
import InfoPanel from '../../common/InfoPanel/InfoPanel';
import { useTranslation } from 'react-i18next';

// Helper to format date as DD-MM-YYYY
function formatDateDMY(dateStr?: string) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
}

const ScenariosSection: React.FC<{ projectId: string; onScenarioCreated?: () => void }> = ({ projectId, onScenarioCreated }) => {
  const { t } = useTranslation(['common', 'screens']);
  const { scenarios, setScenarios, reload } = useProjectScenarios();
  const [dialog, setDialog] = useState<null | { type: 'create' | 'duplicate' | 'remove' | 'rename'; scenario?: ProjectScenario }>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [inputName, setInputName] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedScenarioId, setSelectedScenarioId] = useState<string>(scenarios[0]?.id || '');
  const provider = new ProjectsScenariosRestDataProvider();
  const DEFAULT_NAME = 'Default Scenario';

  // Find the default scenario
  const defaultScenario = scenarios.find(s => s.name === DEFAULT_NAME);

  const openCreate = () => {
    setInputName('');
    setErrorMsg(null);
    setDialog({ type: 'create' });
  };
  const openDuplicate = (scenario: ProjectScenario) => {
    setInputName(scenario.name + ' - clone');
    setErrorMsg(null);
    setDialog({ type: 'duplicate', scenario });
  };
  const openRename = (scenario: ProjectScenario) => {
    setInputName(scenario.name);
    setErrorMsg(null);
    setDialog({ type: 'rename', scenario });
  };
  const openRemove = (scenario: ProjectScenario) => {
    setDialog({ type: 'remove', scenario });
  };
  const closeDialog = () => {
    setDialog(null);
    setInputName('');
    setErrorMsg(null);
  };
  // Helper to check for duplicate scenario names (case-insensitive, except for the scenario being edited/renamed)
  const isDuplicateName = (name: string, excludeId?: string) => {
    return scenarios.some(s => s.name.trim().toLowerCase() === name.trim().toLowerCase() && s.id !== excludeId);
  };

  const handleCreate = async () => {
    const trimmed = inputName.trim();
    if (!trimmed || trimmed === DEFAULT_NAME) return;
    if (isDuplicateName(trimmed)) {
      setErrorMsg(t('screens:projects.scenarios.nameExists'));
      return;
    }
    setLoading(true);
    await provider.create({
      projectId,
      name: trimmed,
      ganttChartPlan: {
        id: null, // Fix: must be null for new scenario
        name: '',
        fromDate: null, // Fix: must be null, not empty string
        toDate: null,   // Fix: must be null, not empty string
        phases: [],
        projectId
      },
      estimateItems: [],
      riskItems: [],
      costGroups: [],
      requirements: [],
      assumptions: []
    });

    await reload();
    setLoading(false);
    closeDialog();
    if (onScenarioCreated) onScenarioCreated();
  };
  const handleDuplicate = async () => {
    if (!dialog?.scenario) return;
    const trimmed = inputName.trim();
    if (!trimmed || trimmed === DEFAULT_NAME) return;
    if (isDuplicateName(trimmed)) {
      setErrorMsg(t('screens:projects.scenarios.nameExists'));
      return;
    }
    setLoading(true);
    const orig = dialog.scenario;
    await provider.create({ ...orig, id: undefined, name: trimmed });
    await reload();
    setLoading(false);
    closeDialog();
    if (onScenarioCreated) onScenarioCreated();
  };

  // Rename scenario handler
  const handleRename = async () => {
    if (!dialog?.scenario) return;
    const trimmed = inputName.trim();
    if (!trimmed || trimmed === DEFAULT_NAME) return;
    if (isDuplicateName(trimmed, dialog.scenario.id)) {
      setErrorMsg(t('screens:projects.scenarios.nameExists'));
      return;
    }
    setLoading(true);
    await provider.update(dialog.scenario.id as string, { ...dialog.scenario, name: trimmed });
    await reload();
    setLoading(false);
    closeDialog();
    if (onScenarioCreated) onScenarioCreated();
  };
  const handleRemove = async () => {
    if (dialog?.scenario?.id && dialog.scenario.name !== DEFAULT_NAME && scenarios.length > 1) {
      setLoading(true);
      await provider.delete(dialog.scenario.id);
      await reload();
      setLoading(false);
      closeDialog();
      // If the removed scenario was selected, switch to default
      if (selectedScenarioId === dialog.scenario.id && defaultScenario) {
        setSelectedScenarioId(defaultScenario.id || '');
      }
      if (onScenarioCreated) onScenarioCreated();
    }
  };

  return (
    <SubsectionPanel
      title={t('screens:projects.scenarios.label')}
      actions={
        <button className="btn btn-sm btn-outline-primary" title={t('screens:projects.scenarios.create')} onClick={openCreate}>
          <FaPlus />
        </button>
      }
    >
      <InfoPanel header="About scenarios">
        Scenarios allow you to create different versions of your project plan. You can duplicate existing scenarios, remove them, or create new ones. Each scenario can have its own Gantt chart, estimates, risks, and cost groups.
        It also allows you to estimate and compare the impact of changes to your project plan without affecting the main project data.
      </InfoPanel>
      <div className="scenario-list" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {scenarios.length === 0 && <div className="text-muted">{t('screens:projects.scenarios.noScenariosYet')}</div>}
        {scenarios.map(scenario => (
          <div key={scenario.id} className="d-flex align-items-center justify-content-between scenario-row" style={{ border: '1px solid #eee', borderRadius: 6, padding: '0.5rem 1rem', background: '#fafbfc' }}>
            <span>{scenario.name}</span>
            <span style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-sm btn-outline-secondary" title={t('screens:projects.scenarios.duplicate')} onClick={() => openDuplicate(scenario)}><FaCopy /></button>
              <button className="btn btn-sm btn-outline-secondary" title={t('screens:projects.scenarios.rename')} onClick={() => openRename(scenario)} disabled={scenario.name === DEFAULT_NAME}><FaEdit /></button>
              <button className="btn btn-sm btn-outline-danger" title={t('screens:projects.scenarios.remove')} onClick={() => openRemove(scenario)} disabled={scenario.name === DEFAULT_NAME || scenarios.length === 1}><FaTrash /></button>
            </span>
          </div>
        ))}
      </div>
      {/* Dialogs */}
      <PopupDialog
        open={!!dialog && (dialog.type === 'create' || dialog.type === 'duplicate' || dialog.type === 'rename')}
        title={
          dialog?.type === 'create' ? t('screens:projects.scenarios.createDialog')
            : dialog?.type === 'duplicate' ? t('screens:projects.scenarios.duplicateDialog')
            : dialog?.type === 'rename' ? t('screens:projects.scenarios.renameDialog')
            : ''
        }
        onClose={closeDialog}
        actions={
          <>
            <button className="btn btn-secondary" onClick={closeDialog} disabled={loading}>{t('common:cancel')}</button>
            {dialog?.type === 'create' && (
              <button className="btn btn-primary" onClick={handleCreate} disabled={!inputName.trim() || inputName.trim() === DEFAULT_NAME || loading}>{t('common:create')}</button>
            )}
            {dialog?.type === 'duplicate' && (
              <button className="btn btn-primary" onClick={handleDuplicate} disabled={!inputName.trim() || inputName.trim() === DEFAULT_NAME || loading}>{t('screens:projects.scenarios.duplicate')}</button>
            )}
            {dialog?.type === 'rename' && (
              <button className="btn btn-primary" onClick={handleRename} disabled={!inputName.trim() || inputName.trim() === DEFAULT_NAME || loading}>{t('common:save')}</button>
            )}
          </>
        }
      >
        <div className="form-group">
          <label>{t('screens:projects.scenarios.scenarioName')}</label>
          <input
            type="text"
            className="form-control"
            value={inputName}
            onChange={e => { setInputName(e.target.value); setErrorMsg(null); }}
            autoFocus
            disabled={loading || (dialog?.scenario?.name === DEFAULT_NAME && dialog?.type !== 'create')}
            placeholder={dialog?.type === 'create' ? t('screens:projects.scenarios.scenarioName') : ''}
          />
          {inputName.trim() === DEFAULT_NAME && <div className="text-danger mt-1">{t('screens:projects.scenarios.cannotUseReservedName')}</div>}
          {errorMsg && <div className="text-danger mt-1">{errorMsg}</div>}
        </div>
      </PopupDialog>
      <PopupDialog
        open={!!dialog && dialog.type === 'remove'}
        title={t('screens:projects.scenarios.rename')}
        onClose={closeDialog}
        actions={
          <>
            <button className="btn btn-secondary" onClick={closeDialog} disabled={loading}>{t('common:cancel')}</button>
            <button className="btn btn-danger" onClick={handleRemove} disabled={loading || dialog?.scenario?.name === DEFAULT_NAME || scenarios.length === 1}>{t('screens:projects.scenarios.remove')}</button>
          </>
        }
      >
        <div>Are you sure you want to remove scenario <b>{dialog?.scenario?.name}</b>?</div>
        {dialog?.scenario?.name === DEFAULT_NAME && <div className="text-danger mt-1">{t('screens:projects.scenarios.defaultCannotBeRemoved')}</div>}
        {scenarios.length === 1 && <div className="text-danger mt-1">{t('screens:projects.scenarios.mustHaveAtLeastOne')}</div>}
      </PopupDialog>
    </SubsectionPanel>
  );
};

const ProjectGeneral: React.FC<{ project: any; infoFields: any[]; onEdit: () => void; onScenarioCreated?: () => void }> = ({ project, infoFields, onEdit, onScenarioCreated }) => {
  const { t } = useTranslation(['common', 'screens']);

  // Render infoFields, formatting dates as DD-MM-YYYY
  const renderFieldValue = (field: any) => {
    if (field.label && (field.label.toLowerCase().includes('date') || field.label.toLowerCase().includes('datum'))) {
      return formatDateDMY(field.value && typeof field.value === 'string' ? field.value : undefined);
    }
    return field.value;
  };

  return (
    <>
      <SubsectionPanel
        title={t('screens:projects.generalProjectInformation')}
        actions={
          <RoleGuard roles={UserRoles.Admin + "," + UserRoles.Manager}>
            <button className="btn btn-outline-secondary" onClick={onEdit} title={t('screens:projects.editProjectDetails')}>
              <FaEdit /> {t('screens:projects.editProjectDetails')}
            </button>
          </RoleGuard>
        }
        className="general-info"
      >
        <div className="info-grid">
          {infoFields.map((field, idx) => (
            <div key={idx}><span className="info-label">{field.label}:</span> {renderFieldValue(field)}</div>
          ))}
        </div>
      </SubsectionPanel>
      <ProjectScenariosProvider projectId={project.id}>
        <ScenariosSection projectId={project.id} onScenarioCreated={onScenarioCreated} />
      </ProjectScenariosProvider>
    </>
  );
};

export default ProjectGeneral;
